import { Component, EventEmitter, Input, Output, SimpleChanges, ViewChild } from '@angular/core';
import { AgGridAngular } from 'ag-grid-angular';
import { AllCommunityModule, ColDef, FilterModel, GridReadyEvent, ModuleRegistry, RowSelectionOptions, themeQuartz } from 'ag-grid-community';
import { TextInputComponent } from '../text-input/text-input.component';
import { ButtonComponent } from '../button/button.component';
import { CommonModule, NgClass } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { GlobalService } from '../../services/global.service';
import { ConfirmationPopupComponent } from '../confirmation-popup/confirmation-popup.component';
import { PluralPipe } from '../../pipes/plural.pipe';
import { ActivatedRoute } from '@angular/router';
const myTheme = themeQuartz.withParams({
  // ---------- BASIC ----------
  accentColor: "#ffffff",
  backgroundColor: "#FFFFFF",
  chromeBackgroundColor: { ref: "backgroundColor" },
  foregroundColor: "#555B62",
  borderColor: "#E3E8EF",

  // ---------- HEADER ----------
  headerBackgroundColor: "#FFFFFF",
  headerTextColor: "#3F41D1",
  headerFontSize: 15,
  headerFontWeight: 600,
  headerHeight: 50,


  // headerColumnSeparatorWidth: 0,
  columnBorder: false,

  // ---------- BODY ----------
  rowBorder: true,
  rowVerticalPaddingScale: 2,
  cellHorizontalPaddingScale: 0.9,
  fontFamily: { googleFont: "arial" },
  fontSize: 15,

  // ---------- OUTER WRAPPER ----------
  wrapperBorder: false,
  wrapperBorderRadius: 10,
  spacing: 6,

  // ---------- SELECTION ----------
  selectedRowBackgroundColor: "#EEF0FF",

  // ---------- CHECKBOX ----------
  checkboxBorderRadius: 3,
  checkboxUncheckedBorderColor: "#333333",
  checkboxCheckedBorderColor: "#3F41D1",
  checkboxCheckedBackgroundColor: "#3F41D1",
  checkboxCheckedShapeColor: "#FFFFFF",

  // ---------- FILTER BUTTON ----------
  filterPanelApplyButtonBackgroundColor: "#087AD1",
  filterPanelCardSubtleHoverColor: "#065aab",

  // ---------- PAGINATION ----------
  inputBackgroundColor: "#ffffff",
});




ModuleRegistry.registerModules([AllCommunityModule]); // Angular Data Grid Component

@Component({
  selector: 'app-ag-grid-list',
  imports: [AgGridAngular, ButtonComponent, CommonModule, FormsModule, ConfirmationPopupComponent, NgClass, PluralPipe],
  templateUrl: './ag-grid-list.component.html',
  styleUrl: './ag-grid-list.component.css'
})
export class AgGridListComponent {
  @ViewChild('grid') grid!: AgGridAngular;
  @ViewChild('bulk_delete_confirmation') bulk_delete_confirmation!: ConfirmationPopupComponent;
  @ViewChild('bulk_disable_confirmation') bulk_disable_confirmation!: ConfirmationPopupComponent;

  get headerHeight(): number | undefined {
    if (this.hide_header_when_empty && !this.list?.data?.length) return 0;
    return undefined;
  }

  get rowSelection(): RowSelectionOptions {
    return {
      mode: 'multiRow',
      enableClickSelection: false,
      headerCheckbox: this.show_checkboxes,
      checkboxes: this.show_checkboxes,
    };
  }
  defaultColDef: ColDef = {
    resizable: true,
    sortable: true,
    filter: 'agTextColumnFilter',
    flex: 1,
    minWidth: 120,
    filterParams: {
      filterOptions: ['contains'],
      defaultOption: 'contains',
      suppressAndOrCondition: true
    },
    cellStyle: {
      display: 'flex',
      alignItems: 'center'
    }
  };



  @Input() list: any;
  @Input() colDefs: ColDef[] = []
  @Input() loading = ''
  @Input() itemName = 'Item'
  @Input() params: any = {}
  @Input() main_description = ''
  @Input() description = ''
  @Input() bulk_delete: boolean = true;
  @Input() bulk_disable: boolean = true;
  @Input() delete_permission: boolean | undefined = undefined;
  @Input() disable_permission: boolean | undefined = undefined;
  @Input() show_checkboxes: boolean = true;
  @Input() getRowStyle: ((params: any) => any) | undefined = undefined;
  @Input() height: string = 'calc(100vh - 120px)';
  @Input() rowHeight: number | undefined = undefined;
  @Input() hide_header_when_empty: boolean = false;
  @Input() hide_bulk_toolbar: boolean = false;

  effectiveColDefs: ColDef[] = [];

  private inactiveStatusCol: ColDef = {
    headerName: '',
    field: 'is_active',
    width: 110,
    flex: 0,
    sortable: false,
    filter: false,
    resizable: false,
    suppressHeaderMenuButton: true,
    suppressMovable: true,
    headerClass: 'inactive-badge-header',
    cellStyle: { display: 'flex', alignItems: 'center', justifyContent: 'flex-end' },
    cellRenderer: (params: any) => {
      if (params.value === false) {
        return `  <button class="w-[71px] h-[19px] box-border inline-flex items-center justify-center rounded border border-[#A3A3A3] py-1 px-4 bg-[#E0E0E0] text-[10px] font-normal leading-none tracking-[0.02em] capitalize text-[#212121] cursor-pointer whitespace-nowrap">Inactive</button>`;
      }
      return '';
    }
  };

  internalGetRowStyle = (params: any) => {
    const custom = this.getRowStyle ? this.getRowStyle(params) : {};
    if (params.data?.is_active === false) {
      return { ...custom, background: '#F5F5F5' };
    }
    return custom || undefined;
  };

  private buildEffectiveColDefs() {
    this.effectiveColDefs = [...this.colDefs];
  }

  @Output() fetch_new_list = new EventEmitter<any>();
  @Output() modified = new EventEmitter<any>();
  @Output() bulkDelete = new EventEmitter<number[]>();
  @Output() bulkDisable = new EventEmitter<number[]>();

  theme = myTheme

  overlayNoRowsTemplate = '';



  gridApi: any;

  latest_filter: any;

  pagination = {
    page: 1,
    page_size: 10,
  }
  total_pages = 1

  get showingFrom(): number { return (this.pagination.page - 1) * this.pagination.page_size + 1; }
  get showingTo(): number { return Math.min(this.pagination.page * this.pagination.page_size, this.list?.count || 0); }

  change_page_size() {
    this.pagination.page = 1;
    this.total_pages = Math.ceil((this.list?.count || 1) / this.pagination.page_size);
    this.fetch_new_list.emit(this.get_query_params());
  }

  pending_delete_ids: number[] = [];
  pending_disable_ids: number[] = [];
  selected_ids_count = 0;
  selected_ids: any = [];

  constructor(public gs: GlobalService, public ar: ActivatedRoute) { }
  ngOnChanges(changes: SimpleChanges) {
    if (changes['list'] && this.list) {
      this.total_pages = Math.ceil((this.list?.count || 1) / this.pagination.page_size)
    }
    if (changes['colDefs']) {
      this.buildEffectiveColDefs();
      if (this.gridApi) {
        this.gridApi.setGridOption('columnDefs', this.effectiveColDefs);
      }
    }
  }

  private _initialQueryParamFire = true;

  ngOnInit(): void {
    this.ar.queryParams.subscribe(async params => {
      this.params = { ...params }
      this.pagination.page = 1;
      if (this._initialQueryParamFire) {
        this._initialQueryParamFire = false;
        return;
      }
      this.fetch_new_list.emit(this.get_query_params());
    })
    this.overlayNoRowsTemplate = `
  <div class="flex flex-col items-center justify-center h-full">
  ${this.main_description != '' || this.description != '' ? '' : '<img  src="assets/no-records.gif" alt="No data" class="w-40 h-auto opacity-80"/>'}
    <span class="mt-4 text-gray-900 text-2xl font-medium"> ${this.main_description !== '' ? this.main_description : ''}</span>
    <p class="mt-2 text-gray-400 text-base">
      ${this.description !== '' ? this.description : 'No Records found'}
    </p>
  </div>
`;
  }

  latest_sort_by: string | undefined;
  latest_sort_order: string | undefined;

  onSortChanged(event: any) {
    const sortState = this.gridApi.getColumnState().filter((s: any) => s.sort);

    if (sortState.length > 0) {
      const primarySort = sortState[0];
      this.latest_sort_by = primarySort.colId;
      this.latest_sort_order = primarySort.sort?.toUpperCase() === 'ASC' ? 'ASC' : 'DESC';
    } else {
      this.latest_sort_by = undefined;
      this.latest_sort_order = undefined;
    }

    this.pagination.page = 1; // Reset to first page when sorting
    this.fetch_new_list.emit(this.get_query_params());
  }

  onFilterChanged() {
    const filterModel = this.grid.api.getFilterModel();
    const mongo_filter_object = agGridFilterToMongo(filterModel);
    this.latest_filter = mongo_filter_object;
    this.pagination.page = 1; // Reset to first page when filtering
    this.fetch_new_list.emit(this.get_query_params());
  }

  grid_ready(params: GridReadyEvent) {
    this.gridApi = params.api;
    this.fetch_new_list.emit(this.get_query_params())
  }

  getVisibleRowCount(): number {
    const rowHeight = this.gridApi.getSizesForCurrentTheme().rowHeight;
    const { top, bottom } = this.gridApi.getVerticalPixelRange();
    const viewportPx = bottom - top;
    return Math.floor(viewportPx / rowHeight);
  }

  change_pagination(direction: string) {
    if (direction == 'next') {
      if (this.pagination.page == this.total_pages) {
        return
      }
      this.pagination.page = this.pagination.page + 1
    } else if (direction == 'previous') {
      if (this.pagination.page == 1) {
        return
      }
      this.pagination.page = this.pagination.page - 1
    } else if (direction == 'first') {
      if (this.pagination.page == 1) {
        return
      }
      this.pagination.page = 1
    } else if (direction == 'last') {
      if (this.pagination.page == this.total_pages) {
        return
      }
      this.pagination.page = this.total_pages
    }
    this.fetch_new_list.emit(this.get_query_params())

  }

  get_query_params() {
    return {
      ...this.params,
      page: this.pagination.page,
      page_size: this.pagination.page_size,
      skip: (this.pagination.page - 1) * this.pagination.page_size,
      limit: this.pagination.page_size,

      sort_by: this.latest_sort_by,
      sort_order: this.latest_sort_order,
      filter: this.latest_filter,

    };
  }

  cell_changed(event: any) {
    this.modified.emit(event.data);
  }

  delete_selected_rows() {
    const selected_item = this.grid.api.getSelectedNodes();
    const selected_data = selected_item.map(item => item.data);
    const selected_ids = selected_data.map(item => item.id || item.user_id);

    if (selected_ids.length === 0) {
      this.gs.toastr_shows_function(`Please select at least one ${this.itemName} to delete.`, 'Warning', 'warning');
      return;
    }

    this.pending_delete_ids = selected_ids;
    this.selected_ids_count = selected_ids.length;
    this.selected_ids = selected_ids;
    this.bulk_delete_confirmation.open();
  }

  confirm_bulk_delete() {
    this.bulkDelete.emit(this.pending_delete_ids);
    this.bulk_delete_confirmation.close();
    this.pending_delete_ids = [];
    this.selected_ids_count = 0;
  }

  onSelectionChanged(event: any) {
    this.selected_ids_count = this.gridApi.getSelectedRows().length;
    const selected_item = this.grid.api.getSelectedNodes();
    const selected_data = selected_item.map(item => item.data);
    const selected_ids = selected_data.map(item => item.id || item.user_id);
    this.selected_ids = selected_ids;
  }

  clear_selection() {
    this.grid.api.deselectAll();
    this.selected_ids_count = 0;
    this.selected_ids = [];
  }

  disable_selected_rows() {
    const selected_item = this.grid.api.getSelectedNodes();
    const selected_data = selected_item.map(item => item.data);
    const selected_ids = selected_data.map(item => item.id || item.user_id);

    if (selected_ids.length === 0) {
      this.gs.toastr_shows_function(`Please select at least one ${this.itemName} to disable.`, 'Warning', 'warning');
      return;
    }

    this.pending_disable_ids = selected_ids;
    this.selected_ids_count = selected_ids.length;
    this.selected_ids = selected_ids;
    this.bulk_disable_confirmation.open();
  }

  confirm_bulk_disable() {
    this.bulkDisable.emit(this.pending_disable_ids);
    this.bulk_disable_confirmation.close();
    this.pending_disable_ids = [];
    this.clear_selection();
  }
}
function agGridFilterToMongo(filterModel: FilterModel) {
  throw new Error('Function not implemented.');
}

